import { z } from 'zod';
import { EMPLOYEE_STATUSES, EMPLOYMENT_TYPES, GENDERS } from '../constants';
import {
  addressSchema,
  objectIdSchema,
  optionalDate,
  optionalEmail,
  optionalObjectId,
  optionalPhone,
  optionalText,
  paginationQuerySchema,
  passwordSchema,
  patchOf,
  requiredEmail,
} from './common';

const emptyToNull = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? null : v);

export const employeeAccountSchema = z.object({
  email: requiredEmail(),
  temporaryPassword: passwordSchema,
  roleIds: z.array(objectIdSchema).min(1).max(10),
});

export const employeeCreateSchema = z.object({
  employeeCode: optionalText(30),
  firstName: z.string().trim().min(1).max(60),
  lastName: optionalText(60),
  officialEmail: optionalEmail(),
  personalEmail: optionalEmail(),
  phone: optionalPhone(),
  gender: z.enum(GENDERS).nullable().optional(),
  dateOfBirth: optionalDate(),
  dateOfJoining: z.coerce.date(),
  branchId: optionalObjectId(),
  departmentId: optionalObjectId(),
  designationId: optionalObjectId(),
  reportingManagerId: optionalObjectId(),
  employmentType: z.enum(EMPLOYMENT_TYPES).default('full_time'),
  status: z.enum(EMPLOYEE_STATUSES).default('active'),
  probationEndsOn: optionalDate(),
  address: addressSchema.optional(),
  emergencyContact: z
    .object({ name: optionalText(100), relation: optionalText(50), phone: optionalPhone() })
    .partial()
    .optional(),
  notes: optionalText(4000),
  /** Optionally create a login for this employee at the same time. */
  account: employeeAccountSchema.nullable().optional(),
});
export type EmployeeCreateInput = z.input<typeof employeeCreateSchema>;

export const employeeUpdateSchema = patchOf(employeeCreateSchema.omit({ account: true }));
export type EmployeeUpdateInput = z.input<typeof employeeUpdateSchema>;

/** Sensitive identity / bank values. Validated, encrypted at rest, masked on read. */
export const employeeSensitiveSchema = z.object({
  pan: z.preprocess(
    emptyToNull,
    z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{5}\d{4}[A-Z]$/, 'Invalid PAN format')
      .nullable()
      .optional(),
  ),
  aadhaar: z.preprocess(
    (v) => (typeof v === 'string' ? (v.replace(/\s/g, '') === '' ? null : v.replace(/\s/g, '')) : v),
    z
      .string()
      .regex(/^\d{12}$/, 'Aadhaar must be 12 digits')
      .nullable()
      .optional(),
  ),
  bankAccountNumber: z.preprocess(
    emptyToNull,
    z
      .string()
      .trim()
      .regex(/^\d{6,20}$/, 'Account number must be 6–20 digits')
      .nullable()
      .optional(),
  ),
  bankIfsc: z.preprocess(
    emptyToNull,
    z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC')
      .nullable()
      .optional(),
  ),
  bankName: optionalText(100),
  accountHolderName: optionalText(100),
});
export type EmployeeSensitiveInput = z.input<typeof employeeSensitiveSchema>;

export const employeeListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(EMPLOYEE_STATUSES).optional(),
  departmentId: objectIdSchema.optional(),
  branchId: objectIdSchema.optional(),
  designationId: objectIdSchema.optional(),
  employmentType: z.enum(EMPLOYMENT_TYPES).optional(),
});

export const branchSchema = z.object({
  name: z.string().trim().min(1).max(100),
  code: optionalText(20),
  address: addressSchema.optional(),
  phone: optionalPhone(),
  isHeadOffice: z.boolean().default(false),
  isActive: z.boolean().default(true),
});
export type BranchInput = z.input<typeof branchSchema>;
export const branchUpdateSchema = patchOf(branchSchema);

export const departmentSchema = z.object({
  name: z.string().trim().min(1).max(100),
  code: optionalText(20),
  description: optionalText(500),
  branchId: optionalObjectId(),
  managerUserId: optionalObjectId(),
  isActive: z.boolean().default(true),
});
export type DepartmentInput = z.input<typeof departmentSchema>;
export const departmentUpdateSchema = patchOf(departmentSchema);

export const designationSchema = z.object({
  name: z.string().trim().min(1).max(100),
  departmentId: optionalObjectId(),
  level: z.coerce.number().int().min(0).max(20).nullable().optional(),
  responsibilities: optionalText(2000),
  isActive: z.boolean().default(true),
});
export type DesignationInput = z.input<typeof designationSchema>;
export const designationUpdateSchema = patchOf(designationSchema);

export const workSessionListQuerySchema = paginationQuerySchema.extend({
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  userId: z.union([objectIdSchema, z.literal('me')]).optional(),
  departmentId: objectIdSchema.optional(),
  state: z.enum(['active', 'idle', 'on_break', 'ended', 'live']).optional(),
});

export const attendanceReportQuerySchema = z.object({
  from: z.coerce.date(),
  to: z.coerce.date(),
  userId: z.union([objectIdSchema, z.literal('me')]).optional(),
  departmentId: objectIdSchema.optional(),
  branchId: objectIdSchema.optional(),
});
